/**
 * Platform-wide live-trading master switch (operator surface, admin auth).
 *
 * The GLOBAL kill-switch: with it OFF, NO subscription places a real order
 * regardless of per-strategy arming. DB-backed + audited. Replaces the host-file
 * `global_enabled`.
 *
 * Backed by:
 *   GET /admin/platform/live-trading
 *   PUT /admin/platform/live-trading  { enabled, reason }
 *
 * `attempts: 1` — a deliberate, audited operator action; never silently retried.
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get, put } from './client';

export const platformLiveSchema = z.object({
  enabled: z.boolean(),
  updated_by: z.string().nullable().optional(),               // operator id (raw)
  updated_by_email: z.string().nullable().optional(),         // resolved
  updated_by_display_name: z.string().nullable().optional(),  // resolved
  updated_at: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
});
export type PlatformLive = z.infer<typeof platformLiveSchema>;

export const getLiveTrading = (
  signal?: AbortSignal,
): Promise<ApiResult<PlatformLive>> =>
  get('/admin/platform/live-trading', platformLiveSchema, {
    signal, retry: { attempts: 1 },
  }) as Promise<ApiResult<PlatformLive>>;

export const setLiveTrading = (
  enabled: boolean,
  reason: string,
  signal?: AbortSignal,
): Promise<ApiResult<PlatformLive>> =>
  put('/admin/platform/live-trading', platformLiveSchema, { enabled, reason }, {
    signal, retry: { attempts: 1 },
  }) as Promise<ApiResult<PlatformLive>>;

export const platformLiveQueryKey = ['admin-platform-live'] as const;

// ─── HARD FREEZE (block ALL orders, incl. exits) — same shape, separate flag ──
export const getHardFreeze = (
  signal?: AbortSignal,
): Promise<ApiResult<PlatformLive>> =>
  get('/admin/platform/hard-freeze', platformLiveSchema, {
    signal, retry: { attempts: 1 },
  }) as Promise<ApiResult<PlatformLive>>;

export const setHardFreeze = (
  enabled: boolean,
  reason: string,
  signal?: AbortSignal,
): Promise<ApiResult<PlatformLive>> =>
  put('/admin/platform/hard-freeze', platformLiveSchema, { enabled, reason }, {
    signal, retry: { attempts: 1 },
  }) as Promise<ApiResult<PlatformLive>>;

export const hardFreezeQueryKey = ['admin-platform-hard-freeze'] as const;
